import { REST_MSG, TEMPLATE_MSG, type EstimateTemplate, type EstimateTemplateList, type EstimateTemplateSummary } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, messageOf } from "../api.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { GroupInputRow, GroupList, GroupRow, GroupSection, GroupTwoLineRow } from "../components/GroupList.jsx";
import { SettingsHead, useRestPageClass } from "../components/SettingsHead.jsx";
import { Sheet } from "../components/Sheet.jsx";
import { ErrorText, guardComposingEnter, useToast } from "../components/ui.jsx";

/**
 * 設定 → 見積書の雛形（023。#197 で「見積の雛形」から改名）。よく使う見積の中身（行の並び・紙の備考）を保存しておき、
 * 「新しい見積書 → 雛形から」で下書きの初期値にする。上限はプランで決まる（一覧の limit）。担当者も使える
 * #156（docs/65 §6・★4 a、Figma「設定の残り（10/9）」㉒㉓）で iOS 風に：
 *   ・上の帯「‹ 設定｜編集」（編集中は「完了」。0件のときは出さない）。行＝2行の行（名前・「12行・10/5 更新」（空なら「まだ空・10/5 更新」）・›）。
 *     押すと雛形の編集（/settings/templates/:id。#197 から設定の2列に入れず全幅＝TemplateEdit.tsx）
 *   ・注「3件／上限 5件」（上限なら朱で TEMPLATE_MSG.limitReached）
 *   ・下のまとまりに「＋ 雛形を作成」（#197。上限なら押せない）→ シート「取消｜雛形を作成｜作成」（タイトルだけ）→ 作成で雛形の編集へ
 *   ・削除は「編集」の ⊖ → 今の確かめ（作った見積は変わらない）。行ごとの「削除」ボタンはやめた（★4）
 */
export function TemplatesPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  const q = useQuery({ queryKey: ["templates"], queryFn: () => api<EstimateTemplateList>("GET", "/estimate-templates") });
  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ 設定
  const back = useBackNav({ to: "/settings", label: "設定" });
  usePageName(REST_MSG.templatesTitle, "見積書テンプレート"); // 戻るボタンは「を」「の」なし（#240）
  const cls = useRestPageClass("tplpage");
  const items = q.data?.items ?? [];
  const limit = q.data?.limit ?? 0;
  const full = !!q.data && items.length >= limit;
  const [editing, setEditing] = useState(false);
  /** 新しい雛形のシート（開いているあいだの中身） */
  const [newSheet, setNewSheet] = useState<{ open: boolean; title: string; error?: string | undefined }>({ open: false, title: "" });
  const [delDlg, setDelDlg] = useState<EstimateTemplateSummary | null>(null);

  const create = useMutation({
    mutationFn: (title: string) => api<EstimateTemplate>("POST", "/estimate-templates", { title, sheets: [], lines: [] }),
    onSuccess: (t) => {
      qc.setQueryData(["template", t.id], t);
      void qc.invalidateQueries({ queryKey: ["templates"] });
      setNewSheet({ open: false, title: "" });
      nav(`/settings/templates/${t.id}`);
    },
    onError: (e) => setNewSheet((d) => ({ ...d, error: messageOf(e) })),
  });
  const del = useMutation({
    mutationFn: (id: string) => api<void>("DELETE", `/estimate-templates/${id}`),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: ["template", id] });
      qc.setQueryData<EstimateTemplateList>(["templates"], (d) => (d ? { ...d, items: d.items.filter((x) => x.id !== id) } : d));
      void qc.invalidateQueries({ queryKey: ["templates"] });
      setDelDlg(null);
      if (items.length <= 1) setEditing(false);
      toast(REST_MSG.templateRemoved);
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  const submitNew = () => {
    const v = newSheet.title.trim();
    if (!v) {
      setNewSheet({ ...newSheet, error: TEMPLATE_MSG.titleRequired });
      document.getElementById("tplTitle")?.focus();
      return;
    }
    if (!create.isPending) create.mutate(v);
  };

  const editBtn = items.length ? (
    <button type="button" className={"btn text cotop-btn" + (editing ? " cotop-done" : "")} onClick={() => setEditing(!editing)} aria-pressed={editing} data-testid="templates-edit">
      {editing ? REST_MSG.done : REST_MSG.edit}
    </button>
  ) : null;

  return (
    <div className={cls} data-testid="templates">
      <SettingsHead back={back} title={REST_MSG.templatesTitle} end={editBtn} />
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      {q.data ? (
        <GroupSection
          note={
            <span data-testid="templates-count">
              {REST_MSG.templateCount.replace("{n}", String(items.length)).replace("{limit}", String(limit))}
              {full ? <span className="shu">{`　${TEMPLATE_MSG.limitReached(limit)}`}</span> : null}
              {editing ? <span className="gsecnote-more">{REST_MSG.templateEditNote}</span> : null}
            </span>
          }
          data-testid="templates-sec-list"
        >
          {items.length ? (
            <GroupList plain id="templateList">
              {items.map((x) => {
                const d = new Date(x.updatedAt);
                const day = `${d.getMonth() + 1}/${d.getDate()}`;
                return (
                  <GroupTwoLineRow
                    key={x.id}
                    title={x.title}
                    sub={x.lineCount ? REST_MSG.templateLines.replace("{n}", String(x.lineCount)).replace("{md}", day) : REST_MSG.templateEmpty.replace("{md}", day)}
                    to={`/settings/templates/${x.id}`}
                    editing={editing}
                    onRemove={() => setDelDlg(x)}
                    removeLabel={REST_MSG.templateRemove.replace("{title}", x.title)}
                    removeTestId="template-remove"
                    linkTestId="template-link"
                    testId="template-row"
                    data-template={x.id}
                  />
                );
              })}
            </GroupList>
          ) : (
            <GroupList plain>
              <GroupRow title={<span className="muted">{REST_MSG.templatesNone}</span>} className="grow-set" data-testid="templates-empty" />
            </GroupList>
          )}
        </GroupSection>
      ) : null}
      {q.data ? (
        <GroupSection data-testid="templates-sec-new">
          <GroupList>
            <GroupRow
              className="co-addrow"
              icon={
                <span className="co-plus" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </span>
              }
              title={REST_MSG.newTemplate}
              onClick={() => setNewSheet({ open: true, title: "" })}
              disabled={full}
              buttonTestId="template-new"
            />
          </GroupList>
        </GroupSection>
      ) : null}

      <Sheet
        open={newSheet.open}
        title={REST_MSG.newTemplate}
        cancelLabel="取消"
        doneLabel={REST_MSG.create}
        doneDisabled={create.isPending}
        onCancel={() => setNewSheet({ open: false, title: newSheet.title })}
        onDone={submitNew}
        testId="template-sheet"
        doneTestId="template-create"
      >
        <ErrorText>{newSheet.error}</ErrorText>
        <GroupSection note={REST_MSG.templateSheetNote}>
          <GroupList plain>
            <GroupInputRow id="tplTitle" label={REST_MSG.templateTitle} wide>
              <input id="tplTitle" className="giinp" maxLength={100} value={newSheet.title} onChange={(e) => setNewSheet({ ...newSheet, title: e.target.value, error: undefined })} onKeyDown={guardComposingEnter} />
            </GroupInputRow>
          </GroupList>
        </GroupSection>
      </Sheet>

      {delDlg ? (
        <div className="dim" onClick={(e) => e.target === e.currentTarget && setDelDlg(null)}>
          <div className="dlg" role="alertdialog" aria-label={REST_MSG.templateRemoveTitle} onKeyDown={(e) => e.key === "Escape" && setDelDlg(null)}>
            <div className="dh">「{delDlg.title}」を削除しますか</div>
            <div className="db">
              <p style={{ margin: 0 }}>{REST_MSG.templateRemoveBody.replace("{n}", String(delDlg.lineCount))}</p>
              <p className="hint" style={{ margin: 0 }}>{REST_MSG.templateRemoveHint}</p>
            </div>
            <div className="df">
              <button type="button" className="btn primary" autoFocus onClick={() => setDelDlg(null)}>
                取消
              </button>
              <button type="button" className="btn" style={{ borderColor: "var(--shu)", color: "var(--shu)" }} disabled={del.isPending} onClick={() => del.mutate(delDlg.id)}>
                削除
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {toastNode}
    </div>
  );
}
